import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

const ShowCatelog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [catalogInfos, setCatalogInfos] = useState([]);
  const [catalogStructures, setCatalogStructures] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [message, setMessage] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showFailedMessage, setShowFailedMessage] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRefs = useRef({});

  // Fetch Catalog Info Data
  const fetchCatalogInfoData = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");
    const apiUrl = "https://api.dqr-display-tms.bonrix.in/api/CatalogInfoOperation/SubUser/Search";
    const requestBody = {
      catalogid: id,
      ispageload: true,
      pagesize: 1000,
      currentpageno: 0,
      withoutpagination: true,
      pagedirection: "first" // Fixed typo: was "frist"
    };
    const headers = {
      accept: "*/*",
      UserId: userid,
      "X-Authorization": token,
      "Content-Type": "application/json-patch+json"
    };
    try {
      const response = await axios.post(apiUrl, requestBody, { headers });
      if (response.data && Array.isArray(response.data.data)) {
        setCatalogInfos(response.data.data);
      } else {
        setCatalogInfos([]);
      }
    } catch (error) {
      console.error("Error fetching Catalogue info:", error);
      setCatalogInfos([]);
    }
  };

  // Fetch Catalog Structure Data
  const fetchCatalogStructureData = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");
    const apiUrl = "https://api.dqr-display-tms.bonrix.in/api/CatalogStructureOperation/SubUser/Search";
    const requestBody = {
      id: 0,
      catalogid: id,
      ispageload: false,
      pagesize: 1000,
      currentpageno: 0,
      withoutpagination: false,
      pagedirection: "first"
    };
    const headers = {
      accept: "*/*",
      UserId: userid,
      "X-Authorization": token,
      "Content-Type": "application/json-patch+json"
    };
    try {
      const response = await axios.post(apiUrl, requestBody, { headers });
      if (response.data && Array.isArray(response.data.data)) {
        setCatalogStructures(response.data.data);
      } else {
        setCatalogStructures([]);
      }
    } catch (error) {
      console.error("Error fetching Catalogue structures:", error);
      setCatalogStructures([]);
    }
  };

  // Merge catalogInfos and catalogStructures — now shows categories even without images
  useEffect(() => {
    // Only return if structures are missing
    if (!catalogStructures.length) {
      setMergedData([]);
      return;
    }

    const grouped = {};

    // Group existing catalogInfos by catalogstructureid
    catalogInfos.forEach((info) => {
      const key = info.catalogstructureid;
      if (!grouped[key]) {
        grouped[key] = { structure: null, items: [] };
      }
      grouped[key].items.push(info);
    });

    // Attach structure data to each group (include structures even if no images)
    catalogStructures.forEach((structure) => {
      const key = structure.id;
      if (!grouped[key]) {
        grouped[key] = {
          structure: structure,
          items: []
        };
      } else {
        grouped[key].structure = structure;
      }
    });

    // Convert to array and sort by position
    const mergedList = Object.values(grouped).sort((a, b) => {
      const posA = a.structure?.position ?? Infinity;
      const posB = b.structure?.position ?? Infinity;
      return posA - posB;
    });

    setMergedData(mergedList);
  }, [catalogInfos, catalogStructures]);

  // Load both datasets on mount
  useEffect(() => {
    fetchCatalogInfoData();
    fetchCatalogStructureData();
  }, [id]);

  // Upload Image Function
  const handleAddImage = async (catalogStructureId, files) => {
    if (!catalogStructureId) {
      console.error("No CatalogueStructureId provided");
      setMessage("No Catalogue structure ID provided");
      setShowFailedMessage(true);
      return;
    }
    if (!files || files.length === 0) {
      console.error("No files selected");
      setMessage("No files selected");
      setShowFailedMessage(true);
      return;
    }

    setIsUploading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");
    const apiUrl = "https://api.dqr-display-tms.bonrix.in/api/CatalogInfoOperation/SubUser/Insert";

    // Process each file individually
    for (let file of Array.from(files)) {
      const isVideo = file.type.startsWith("video/") || isVideoFile(file.name);
      
      const formDataToSend = new FormData();
      formDataToSend.append("CatalogStructureId", catalogStructureId);
      formDataToSend.append("Name", isVideo ? "video" : "car");
      formDataToSend.append("Remark", isVideo ? "video_file" : "test");
      formDataToSend.append("Active", true);
      formDataToSend.append("File", file);

      try {
        const response = await axios.post(apiUrl, formDataToSend, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token
            // Note: No need to set Content-Type; browser does it automatically with boundary for FormData
          }
        });
        console.log("Image uploaded successfully:", response.data);
        fetchCatalogInfoData(); // Refresh data
      } catch (error) {
        console.error("Error uploading image:", error.response?.data || error.message);
      }
    }
    
    // Refresh data after all uploads
    setTimeout(() => {
      fetchCatalogInfoData();
    }, 500);
    setIsUploading(false);
  };

  // Delete Image
  const handleDeleteImage = async (id) => {
    const token = getCookie("token");
    const userid = getCookie("userid");
    const apiUrl = "https://api.dqr-display-tms.bonrix.in/api/CatalogInfoOperation/SubUser/Delete";
    try {
      const response = await axios.post(
        apiUrl,
        JSON.stringify({ id }),
        {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
            "Content-Type": "application/json-patch+json"
          }
        }
      );
      fetchCatalogInfoData(); // Refresh after delete
    } catch (error) {
      console.error("Error deleting image:", error.response?.data || error.message);
    }
  };

  // Handle file input change
  const handleFileChange = (e, catalogStructureId) => {
    const files = e.target.files;
    if (files && files.length > 0 && catalogStructureId) {
      handleAddImage(catalogStructureId, files);
    }
    e.target.value = ''; // Allow re-selecting the same file
  };

  // Trigger file input click
  const handleButtonClick = (structureId) => {
    const refKey = `input-${structureId}`;
    if (fileInputRefs.current[refKey]) {
      fileInputRefs.current[refKey].click();
    } else {
      console.error("File input ref not found for:", refKey);
    }
  };

  // Open modal for image preview
  const handleImageClick = (image) => {
    setModalImages([image]);
    setShowModal(true);
    console.log("Opening modal:", { filepath: image, fileType: isVideoFile(image) ? 'video' : 'image' });
  };

  // Check if file is video
  const isVideoFile = (filepath) => {
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    return videoExtensions.some(ext => filepath.toLowerCase().includes(ext));
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setModalImages([]);
  };

  // Close success message
  const closeSuccessMessage = () => {
    setShowSuccessMessage(false);
    setMessage("");
  };

  // Close failed message
  const closeFailedMessage = () => {
    setShowFailedMessage(false);
    setMessage("");
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <h1>Images</h1>
                  <div>
                    <button
                      className="btn btn-primary me-2"
                      onClick={() => navigate(`/show-image-preview/${id}`)}
                    >
                      Show Preview
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => navigate(`/add-catelog-image/${id}`)}
                    >
                      Add Category
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <table className="table table-hover text-nowrap">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Category Name</th>
                    <th>Catalogue Images/Videos</th>
                    <th>Add Image/Video</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedData.length > 0 ? (
                    mergedData.map((group, index) => {
                      const structureName = group.structure?.name || "Unnamed Category";
                      const hasImages = group.items.length > 0;
                      const structureId = group.structure?.id;

                      return (
                        <tr key={structureId || `fallback-${index}`}>
                          <td>{index + 1}</td>
                          <td>
                            <strong>{structureName}</strong>
                          </td>
                          <td>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                              {hasImages ? (
                                group.items.map((item) => {
                                  const isVideo = isVideoFile(item.filepath);
                                  return (
                                    <div key={item.id} style={{ position: "relative" }}>
                                      {isVideo ? (
                                        <video
                                          src={item.filepath}
                                          style={{
                                            width: "80px",
                                            height: "80px",
                                            objectFit: "cover",
                                            cursor: "pointer"
                                          }}
                                          onClick={() => handleImageClick(item.filepath)}
                                          muted
                                          loop
                                          onMouseEnter={(e) => e.target.play()}
                                          onMouseLeave={(e) => e.target.pause()}
                                        />
                                      ) : (
                                        <img
                                          src={item.filepath}
                                          alt={item.name}
                                          style={{
                                            width: "80px",
                                            height: "80px",
                                            objectFit: "cover",
                                            cursor: "pointer"
                                          }}
                                          onClick={() => handleImageClick(item.filepath)}
                                        />
                                      )}
                                      <span
                                        style={{
                                          position: "absolute",
                                          top: "0",
                                          right: "0",
                                          background: "red",
                                          color: "white",
                                          fontSize: "14px",
                                          cursor: "pointer",
                                          padding: "2px 5px",
                                          borderRadius: "50%"
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteImage(item.id);
                                        }}
                                      >
                                        &times;
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <span>No images</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <input
                              type="file"
                              name="images"
                              onChange={(e) => handleFileChange(e, structureId)}
                              multiple
                              accept="image/*,video/*"
                              ref={(el) => (fileInputRefs.current[`input-${structureId}`] = el)}
                              style={{ display: "none" }}
                            />
                            <button
                              type="button"
                              className="btn btn-info btn-sm"
                              onClick={() => handleButtonClick(structureId)}
                              disabled={!structureId || isUploading}
                            >
                              {isUploading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Uploading...
                                </>
                              ) : (
                                "+ Add Media"
                              )}
                            </button>
                            {!structureId && (
                              <small className="text-danger d-block">Invalid ID</small>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No categories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {/* Image/Video Preview Modal */}
      {showModal && modalImages.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px"
          }}
          onClick={closeModal}
        >
          {isVideoFile(modalImages[0]) ? (
            <video
              src={modalImages[0]}
              controls
              autoPlay
              style={{ maxHeight: "80%", maxWidth: "80%" }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={modalImages[0]}
              alt="Preview"
              style={{ maxHeight: "80%", maxWidth: "80%" }}
            />
          )}
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <SuccessMessage 
          message={message} 
          onClose={closeSuccessMessage}
        />
      )}

      {/* Failed Message */}
      {showFailedMessage && (
        <FailedMessage 
          message={message} 
          onClose={closeFailedMessage}
        />
      )}
    </>
  );
};

export default ShowCatelog;

// Utility function to get cookie
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}